import { useEffect, useState } from 'react';
import {
  AUTO_RELOAD_DELAY_MS,
  applyNow,
  consumeJustUpdated,
  subscribeUpdate,
  type UpdateState,
} from '@/lib/appUpdate';
import { formatAppVersion, readAppVersion } from '@/lib/appVersion';
import { ChangelogModal } from '@/features/auth/ChangelogModal';

/**
 * 새 버전이 준비되면 뜨는 알림. (2026-08-30, docs/impl/89)
 *
 * 🔴 조용히 새로고침하지 않는다
 *   화면이 이유 없이 리셋되면 "튕겼다" 로 읽힌다. 무슨 일인지 먼저 알리고
 *   몇 초 뒤에 새로고침한다. 급하면 바로 누를 수 있고, 뭔가 쓰던 중이면
 *   미룰 수 있다.
 *
 * 🔴 화면 밖(앱이 안 보일 때)이면 기다리지 않고 바로 적용한다
 *   폰에서 앱을 다시 여는 순간 확인이 걸리는데, 그때는 이미 사용자가
 *   화면을 보고 있다. 반대로 백그라운드에서 잡힌 경우에는 보여줄 사람이
 *   없으므로 카운트다운이 의미가 없다.
 *
 * 🔴 state.auto 가 false 면 자동으로 적용하지 않는다 (2026-08-30 보강)
 *   직전 갱신이 먹히지 않은 흔적이 있다는 뜻이다. 그대로 자동 적용을 걸면
 *   몇 초마다 새로고침이 반복돼 앱을 아예 못 쓰게 된다. 그때는 알림만 띄우고
 *   사용자가 누르기를 기다린다.
 *
 * 자리
 *   폰   화면 아래. 하단 탭 바를 가리지 않게 그 위로 띄우고,
 *        아이폰 홈 인디케이터 영역(safe-area)을 피한다
 *   PC   오른쪽 아래. 본문을 밀지 않는다
 */

/**
 * 배너 두 번째 줄 문구.
 *
 * 화면에 그리는 것과 판단을 갈라 둔다 - 조건이 네 갈래라 눈으로는 검증이 안 된다.
 *
 * @param seconds  남은 카운트다운 초
 * @param postponed 사용자가 "나중에" 를 눌렀나
 */
export function updateMessage(state: UpdateState, seconds: number, postponed: boolean): string {
  if (state.applying) {
    // 몇 초 뒤에도 그대로면 워커가 응답하지 않는 것이다. 다시 누르면 강제 갱신으로 간다
    return '새로고침하는 중입니다… 그대로 멈춰 있으면 아래 버튼을 다시 눌러 주세요.';
  }
  if (state.version !== null && !postponed && state.auto) {
    // 받게 될 버전을 아는 경우. 번호를 함께 알린다
    return `${state.version} 버전을 ${seconds}초 뒤 받습니다. 로그인은 유지됩니다.`;
  }
  if (!state.auto) {
    // 자동 적용이 반복 실패하는 상황. 사용자가 직접 눌러야 한다
    return '자동 적용이 되지 않았습니다. 아래 버튼을 눌러 주세요.';
  }
  if (postponed) {
    return '준비되면 아래 버튼을 눌러 주세요. 로그인은 유지됩니다.';
  }
  // 버전 파일이 잡은 경우는 캐시를 비우고 받으므로 몇 초 더 걸린다
  const how = state.source === 'version' ? '최신 상태로 다시 불러옵니다' : '자동으로 새로고침합니다';
  return `${seconds}초 뒤 ${how}. 로그인은 유지됩니다.`;
}

export function UpdateBanner() {
  const [state, setState] = useState<UpdateState>({
    available: false,
    applying: false,
    source: null,
    auto: true,
    version: null,
  });
  const [seconds, setSeconds] = useState(Math.round(AUTO_RELOAD_DELAY_MS / 1000));
  const [postponed, setPostponed] = useState(false);
  /** 방금 갱신됐다면 직전 버전. 무엇이 바뀌었는지 알리는 배너를 띄운다 */
  const [updatedFrom, setUpdatedFrom] = useState<string | null>(null);
  const [changelogOpen, setChangelogOpen] = useState(false);

  useEffect(() => subscribeUpdate(setState), []);

  // 갱신 직후 한 번만 뜬다. consumeJustUpdated 가 표시를 지운다
  useEffect(() => setUpdatedFrom(consumeJustUpdated()), []);

  // 새 버전을 잡으면 카운트다운을 시작한다. 미루거나 자동이 막혔으면 멈춘다.
  useEffect(() => {
    if (!state.available || !state.auto || postponed || state.applying) {
      return;
    }
    // 화면 밖에서 잡힌 것이면 기다릴 이유가 없다
    if (document.hidden) {
      void applyNow();
      return;
    }

    setSeconds(Math.round(AUTO_RELOAD_DELAY_MS / 1000));
    const timer = window.setInterval(() => {
      setSeconds((left) => {
        if (left <= 1) {
          window.clearInterval(timer);
          void applyNow();
          return 0;
        }
        return left - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [state.available, state.applying, state.auto, postponed]);

  const appVersion = readAppVersion(import.meta.env);

  /*
    갱신이 끝난 뒤의 알림. (2026-08-30 사용자 요청)

    🔴 업데이트 **전에** 무엇이 바뀌는지는 보여줄 수 없다
      새 버전의 변경 내역은 새 번들 안에 있어서, 지금 실행 중인 앱은 옛 내역만
      안다. 그래서 갱신을 마치고 새 코드가 뜬 뒤에 알린다 - 이때는 새 내역을
      읽을 수 있다.
  */
  if (updatedFrom !== null && appVersion !== null) {
    return (
      <>
        <div
          role="status"
          aria-live="polite"
          className={
            'fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-50 ' +
            'rounded-xl border border-line bg-surface p-3 shadow-xl ' +
            'sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-80'
          }
        >
          <p className="text-sm font-medium text-fg">
            {formatAppVersion({ version: appVersion.version, commit: '' })} 로 업데이트했습니다
          </p>
          <p className="mt-0.5 text-xs text-fg-muted">이전 버전 {updatedFrom}</p>

          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => setChangelogOpen(true)}
              className={
                'flex-1 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-brand-fg ' +
                'transition-colors hover:bg-brand-hover'
              }
            >
              무엇이 바뀌었나
            </button>
            <button
              type="button"
              onClick={() => setUpdatedFrom(null)}
              className="rounded-lg px-3 py-2 text-sm text-fg-muted transition-colors hover:bg-surface-hover"
            >
              닫기
            </button>
          </div>
        </div>

        <ChangelogModal
          open={changelogOpen}
          onClose={() => {
            setChangelogOpen(false);
            setUpdatedFrom(null);   // 내역을 봤으면 배너는 할 일을 다 했다
          }}
          currentVersion={appVersion.version}
        />
      </>
    );
  }

  if (!state.available) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        // 폰: 하단 탭(약 4rem) 위에 띄운다. PC(sm↑): 오른쪽 아래 고정
        'fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-50 ' +
        'rounded-xl border border-line bg-surface p-3 shadow-xl ' +
        'sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-80'
      }
    >
      <p className="text-sm font-medium text-fg">새 버전이 준비되었습니다</p>
      <p className="mt-0.5 text-xs text-fg-muted">{updateMessage(state, seconds, postponed)}</p>

      <div className="mt-2 flex gap-2">
        {/*
          🔴 적용 중에도 누를 수 있게 둔다 (2026-09-02 사용자 신고)
            전에는 disabled 였다. 워커가 응답하지 않아 "새로고침하는 중입니다…"
            에서 멈추면 버튼까지 잠겨 화면에서 빠져나갈 길이 없었다.
            지금은 다시 누르면 applyNow 가 캐시를 비우는 강제 갱신으로 넘어간다.
        */}
        <button
          type="button"
          onClick={() => void applyNow()}
          className={
            'flex-1 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-brand-fg ' +
            'transition-colors hover:bg-brand-hover'
          }
        >
          {state.applying ? '다시 시도' : '지금 새로고침'}
        </button>
        {!postponed && state.auto && !state.applying && (
          <button
            type="button"
            onClick={() => setPostponed(true)}
            className="rounded-lg px-3 py-2 text-sm text-fg-muted transition-colors hover:bg-surface-hover"
          >
            나중에
          </button>
        )}
      </div>
    </div>
  );
}
